import { AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  disconnectGoogleSso,
  disconnectMicrosoftSso,
  getGoogleAllowedUsers,
  getGoogleDirectoryUsers,
  getGoogleSsoStatus,
  getMicrosoftAllowedUsers,
  getMicrosoftDirectoryUsers,
  getMicrosoftSsoStatus,
  revokeGoogleAllowedUser,
  revokeMicrosoftAllowedUser,
  setGoogleAllowedUsers,
  setMicrosoftAllowedUsers,
  startGoogleSsoConnect,
  startMicrosoftSsoConnect,
} from '@api/sso';
import type { AllowedSsoUser, DirectoryUser, SsoConnectionStatus } from '@schemas/api';
import { useNotificationStore } from '@store/notificationStore';

const SSO_STATUS_KEY = ['sso', 'microsoft', 'status'] as const;
const SSO_ALLOWED_USERS_KEY = ['sso', 'microsoft', 'allowed-users'] as const;
const ssoDirectoryUsersKey = (search?: string) => ['sso', 'microsoft', 'directory-users', search] as const;

const GOOGLE_SSO_STATUS_KEY = ['sso', 'google', 'status'] as const;
const GOOGLE_SSO_ALLOWED_USERS_KEY = ['sso', 'google', 'allowed-users'] as const;
const googleSsoDirectoryUsersKey = (search?: string) => ['sso', 'google', 'directory-users', search] as const;

export const useMicrosoftSsoStatus = () => {
  return useQuery<SsoConnectionStatus>({
    queryKey: SSO_STATUS_KEY,
    queryFn: getMicrosoftSsoStatus,
  });
};

export const useStartMicrosoftSsoConnect = () => {
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: startMicrosoftSsoConnect,
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to start Microsoft 365 connection',
        type: 'error',
      });
    },
  });
};

export const useDisconnectMicrosoftSso = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: disconnectMicrosoftSso,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SSO_STATUS_KEY });
      queryClient.invalidateQueries({ queryKey: SSO_ALLOWED_USERS_KEY });
      addNotification({ message: 'Microsoft 365 disconnected', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to disconnect Microsoft 365',
        type: 'error',
      });
    },
  });
};

export const useMicrosoftDirectoryUsers = (search?: string, enabled = true) => {
  return useQuery<DirectoryUser[]>({
    queryKey: ssoDirectoryUsersKey(search),
    queryFn: () => getMicrosoftDirectoryUsers(search),
    enabled,
  });
};

export const useMicrosoftAllowedUsers = () => {
  return useQuery<AllowedSsoUser[]>({
    queryKey: SSO_ALLOWED_USERS_KEY,
    queryFn: getMicrosoftAllowedUsers,
  });
};

export const useSetMicrosoftAllowedUsers = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: setMicrosoftAllowedUsers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SSO_ALLOWED_USERS_KEY });
      queryClient.invalidateQueries({ queryKey: ['sso', 'microsoft', 'directory-users'] });
      addNotification({ message: 'Sign-in access granted', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to grant sign-in access',
        type: 'error',
      });
    },
  });
};

export const useRevokeMicrosoftAllowedUser = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: revokeMicrosoftAllowedUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SSO_ALLOWED_USERS_KEY });
      queryClient.invalidateQueries({ queryKey: ['sso', 'microsoft', 'directory-users'] });
      addNotification({ message: 'Sign-in access revoked', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to revoke sign-in access',
        type: 'error',
      });
    },
  });
};

export const useGoogleSsoStatus = () => {
  return useQuery<SsoConnectionStatus>({
    queryKey: GOOGLE_SSO_STATUS_KEY,
    queryFn: getGoogleSsoStatus,
  });
};

export const useStartGoogleSsoConnect = () => {
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: startGoogleSsoConnect,
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to start Google Workspace connection',
        type: 'error',
      });
    },
  });
};

export const useDisconnectGoogleSso = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: disconnectGoogleSso,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GOOGLE_SSO_STATUS_KEY });
      queryClient.invalidateQueries({ queryKey: GOOGLE_SSO_ALLOWED_USERS_KEY });
      addNotification({ message: 'Google Workspace disconnected', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to disconnect Google Workspace',
        type: 'error',
      });
    },
  });
};

export const useGoogleDirectoryUsers = (search?: string, enabled = true) => {
  return useQuery<DirectoryUser[]>({
    queryKey: googleSsoDirectoryUsersKey(search),
    queryFn: () => getGoogleDirectoryUsers(search),
    enabled,
  });
};

export const useGoogleAllowedUsers = () => {
  return useQuery<AllowedSsoUser[]>({
    queryKey: GOOGLE_SSO_ALLOWED_USERS_KEY,
    queryFn: getGoogleAllowedUsers,
  });
};

export const useSetGoogleAllowedUsers = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: setGoogleAllowedUsers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GOOGLE_SSO_ALLOWED_USERS_KEY });
      queryClient.invalidateQueries({ queryKey: ['sso', 'google', 'directory-users'] });
      addNotification({ message: 'Sign-in access granted', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to grant sign-in access',
        type: 'error',
      });
    },
  });
};

export const useRevokeGoogleAllowedUser = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: revokeGoogleAllowedUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GOOGLE_SSO_ALLOWED_USERS_KEY });
      queryClient.invalidateQueries({ queryKey: ['sso', 'google', 'directory-users'] });
      addNotification({ message: 'Sign-in access revoked', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to revoke sign-in access',
        type: 'error',
      });
    },
  });
};
